export const COLOR_PRESETS = [
  { name: 'Glacier',  primary: '#4488ff', accent: '#00aaff', visor: '#88ddff' },
  { name: 'Ember',    primary: '#ff4422', accent: '#ff8800', visor: '#ffcc44' },
  { name: 'Phantom',  primary: '#9944cc', accent: '#cc66ff', visor: '#dd99ff' },
  { name: 'Toxic',    primary: '#44cc44', accent: '#88ff44', visor: '#ccff88' },
  { name: 'Titanium', primary: '#888899', accent: '#aabbcc', visor: '#ddeeff' },
] as const;

export type ColorPresetIndex = 0 | 1 | 2 | 3 | 4;

export const XP_THRESHOLDS = [
  0, 100, 200, 300, 400, 500, 650, 800, 950, 1100,       // levels 1-10
  1300, 1500, 1700, 1900, 2100, 2350, 2600, 2850, 3100, 3350, // 11-20
  3650, 3950, 4250, 4550, 4850, 5200, 5550, 5900, 6250, 6600, // 21-30
  7000, 7400, 7800, 8200, 8600, 9050, 9500, 9950, 10400, 10850, // 31-40
  11350, 11850, 12350, 12850, 13350, 13900, 14450, 15000, 15550, 16200, // 41-50
];

export interface MatchSummary {
  mapId: string;
  result: 'win' | 'loss' | 'draw';
  kills: number;
  deaths: number;
  score: number;
  timestamp: number;
  eloChange: number;
}

export interface PlayerProfile {
  name: string;
  tag: string;
  colorPreset: ColorPresetIndex;
  preferredArmor: 'light' | 'medium' | 'heavy';
  xp: number;
  level: number;
  elo: number;
  matchHistory: MatchSummary[];
}

const STORAGE_KEY = 'tribes2_profile_v1';

function makeDefaultProfile(): PlayerProfile {
  return {
    name: `Pilot-${Math.floor(Math.random() * 9000) + 1000}`,
    tag: '',
    colorPreset: 0,
    preferredArmor: 'medium',
    xp: 0,
    level: 1,
    elo: 1000,
    matchHistory: [],
  };
}

export function loadProfile(): PlayerProfile {
  if (typeof localStorage === 'undefined') return makeDefaultProfile();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return makeDefaultProfile();
    const parsed = JSON.parse(raw) as PlayerProfile;
    return parsed;
  } catch {
    return makeDefaultProfile();
  }
}

export function saveProfile(profile: PlayerProfile): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
  } catch {
    // ignore storage errors
  }
}

export function computeLevel(xp: number): number {
  let level = 1;
  for (let i = 0; i < XP_THRESHOLDS.length; i++) {
    if (xp >= XP_THRESHOLDS[i]) {
      level = i + 1;
    } else {
      break;
    }
  }
  return Math.min(level, 50);
}

export function xpToNextLevel(xp: number): number {
  const level = computeLevel(xp);
  if (level >= 50) return 0;
  return XP_THRESHOLDS[level] - xp;
}

export function awardMatchXP(
  kills: number,
  deaths: number,
  captures: number,
  won: boolean,
): number {
  return (
    kills * 10 +
    Math.max(0, kills - deaths) * 5 +
    captures * 50 +
    (won ? 100 : 25)
  );
}

export function updateElo(
  currentElo: number,
  won: boolean,
  opponentAvgElo: number,
): { newElo: number; delta: number } {
  const K = 16;
  const expected = 1 / (1 + Math.pow(10, (opponentAvgElo - currentElo) / 400));
  const actual = won ? 1 : 0;
  const delta = Math.round(K * (actual - expected));
  const newElo = Math.max(600, currentElo + delta);
  return { newElo, delta };
}

export function getRankTier(elo: number): { name: string; color: string } {
  if (elo >= 1800) return { name: 'Diamond',  color: '#88eeff' };
  if (elo >= 1500) return { name: 'Platinum', color: '#aaddbb' };
  if (elo >= 1200) return { name: 'Gold',     color: '#ffcc44' };
  if (elo >= 1000) return { name: 'Silver',   color: '#cccccc' };
  return                   { name: 'Bronze',  color: '#cc8844' };
}

export function addMatchToHistory(
  profile: PlayerProfile,
  summary: MatchSummary,
): PlayerProfile {
  const matchHistory = [summary, ...profile.matchHistory].slice(0, 20);
  return { ...profile, matchHistory };
}
